import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getFollowSelectedVehicle, getIsFullMissionRouteVisible, getSelectedVehicle } from 'store/slices/appSlice';

import type { VehicleV2 } from 'API';

const ZOOM_LEVEL = 11;

const useCenterVehicle = (vehicle?: VehicleV2) => {
    const isVehicleFocused = useSelector(getFollowSelectedVehicle);
    const selectedVehicleState = useSelector(getSelectedVehicle);
    const hasMission = useSelector(getIsFullMissionRouteVisible);
    const position = vehicle?.position?.value?.position;
    const latitude = position?.latitude;
    const longitude = position?.longitude;
    const hasCoordinates = typeof latitude === 'number' && typeof longitude === 'number';

    const pvlTriggered = !!selectedVehicleState?.pvlTriggered;
    const selectedVIN = selectedVehicleState?.selectedVIN;

    // Continuous re-centring belongs to the lock alone.
    const shouldFollow = !hasMission && isVehicleFocused;

    const [focusRequest, setFocusRequest] = useState(0);
    const [servedRequest, setServedRequest] = useState(0);
    const previousVIN = useRef(selectedVIN);
    const wasLocked = useRef(isVehicleFocused);

    useEffect(() => {
        const vinChanged = selectedVIN !== previousVIN.current;
        const lockEngaged = isVehicleFocused && !wasLocked.current;
        previousVIN.current = selectedVIN;
        wasLocked.current = isVehicleFocused;

        // While the full mission route is shown, that view owns the viewport.
        if (hasMission || !selectedVIN) {
            return;
        }

        // A new selection focuses only when it came from the search box or the sidebar, so
        // clicking a marker on the map selects without moving the viewport.
        if (lockEngaged || (vinChanged && (pvlTriggered || isVehicleFocused))) {
            setFocusRequest(request => request + 1);
        }
    }, [selectedVIN, isVehicleFocused, pvlTriggered, hasMission]);

    // Hold the request until a position is actually known, so a focus is never wasted on a
    // vehicle whose telemetry has not arrived yet.
    const isFocusing = focusRequest > servedRequest && hasCoordinates;

    useEffect(() => {
        if (isFocusing) {
            setServedRequest(focusRequest);
        }
    }, [isFocusing, focusRequest]);

    const center = useMemo(
        () =>
            (shouldFollow || isFocusing) && typeof latitude === 'number' && typeof longitude === 'number'
                ? { lat: latitude, lng: longitude }
                : undefined,
        [shouldFollow, isFocusing, latitude, longitude]
    );

    // Only ever set on the single commit that serves a focus, so the user's own zoom survives.
    const zoom = isFocusing ? ZOOM_LEVEL : undefined;

    const reset = () => {
        setServedRequest(focusRequest);
    };

    /**
     * Deliberately not animated.
     *
     * UIKit forwards a `zoomAnimation` flag as the second argument of `setLookAtData`, and
     * HERE animates a proper fly-to when it is set — that part works. The problem is that the
     * flight takes about a second, during which `useBoundingBox` can dispatch a fresh bounding
     * box; MapPosition applies it and abandons the flight part-way. Measured over four focus
     * attempts, two landed on the vehicle and two were stranded at an intermediate zoom
     * (8.3 and 9.1 instead of 11).
     *
     * The instant application wins that race by being atomic. Animating reliably would mean
     * making one place own the viewport instead of having the focus and the bounding box
     * compete for it, which is a larger change than a visual nicety warrants.
     */
    return { center, zoom, reset };
};

export default useCenterVehicle;
